import mainStyle from '@/fsd/shared/ui/static/css/mainStyle.module.scss';
import cx from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { lazy, Suspense, useEffect } from 'react';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useProtectRoute } from './fsd/entities/access/model/hooks/useProtectRoute';
import { useLoadClosureMonths } from './fsd/entities/closure-months/model/hooks/useLoadClosureMonths';
import { useFindPurchaseMissing } from './fsd/entities/purchase/model/hooks/useFindPurchaseMissing/useFindPurchaseMissing';
import { ChooseCabinet } from './fsd/pages/choose-cabinet';
import PasswordReset from './fsd/pages/password-reset';
import Remains from './fsd/pages/remains';
import { routes } from './fsd/shared/config/routes/routesConfig';
import EmptyData from './fsd/shared/ui/EmptyData/EmptyData';
import Loader from './fsd/shared/ui/Loader/Loader';
import { Header } from './fsd/widgets/header';
import { TopNotification } from './fsd/widgets/top-notification/ui/TopNotification/TopNotification';
import { useGlobalAlertsNotifications } from '@fsd/widgets/top-notification/';
import { useRealtimeNotifications } from '@fsd/feature/realtime-notifications/';

// DO NOT DELETE
import commonStyle from '@/fsd/shared/ui/static/css/commonStyle.module.scss';
import MutualSettlements from './fsd/pages/mutual-settlements/ui/MutualSettlements/MutualSettlements';
import { SettingsLayout } from '@fsd/widgets/settings-layout/';

// Lazy load all major components
const Returns = lazy(() => import('./fsd/pages/returns'));
const RequestsForSupply = lazy(() =>
  import(
    './fsd/pages/requests-for-supply/ui/RequestsForSupply/RequestsForSupply'
  )
);
const RequestsForSupplyItem = lazy(() =>
  import(
    './fsd/pages/requests-for-supply-item/ui/RequestsForSupplyItem/RequestsForSupplyItem'
  )
);
const MovingItem = lazy(() =>
  import('./fsd/pages/moving-item/ui/MovingItem/MovingItem')
);
const MovingList = lazy(() =>
  import('./fsd/pages/moving/ui/MovingList/MovingList')
);
const Account = lazy(() =>
  import('./fsd/pages/account/ui/Lc/Account/Account.jsx')
);
const AccountStaff = lazy(() => import('./fsd/pages/account-staff'));
const Api = lazy(() => import('./fsd/pages/api'));
const Articles = lazy(() =>
  import('./fsd/pages/account/ui/Lc/Articles/Articles.jsx')
);
const Notices = lazy(() =>
  import('./fsd/pages/account/ui/Lc/Notices/Notices.jsx')
);
const Login = lazy(() => import('./fsd/pages/login'));
const Signup = lazy(() => import('./fsd/pages/sign-up'));
const Advertisement = lazy(() => import('./fsd/pages/advertisement'));
const Projects = lazy(() => import('./fsd/pages/projects'));
const ProjectItem = lazy(() =>
  import('./fsd/pages/projects/ui/ProjectItem/index')
);
const AnsweringEdit = lazy(() =>
  import('./fsd/pages/answering/ui/AnsweringEdit/index')
);
const Answering = lazy(() => import('./fsd/pages/answering'));
const Balance = lazy(() => import('./fsd/pages/balance'));
const Economy = lazy(() => import('./fsd/pages/economy'));
const Article = lazy(() => import('./fsd/pages/faq/ui/Article'));
const Categories = lazy(() => import('./fsd/pages/faq/ui/Categories.jsx'));
const Faq = lazy(() => import('./fsd/pages/faq/ui/Faq'));
const Banks = lazy(() => import('./fsd/pages/banks'));
const Cashboxes = lazy(() =>
  import('./fsd/pages/cashboxes/')
);
const DDS = lazy(() => import('./fsd/pages/dds'));
const Loans = lazy(() => import('./fsd/pages/loans'));
const Rules = lazy(() => import('./fsd/pages/rules'));
const FinancingReport = lazy(() => import('./fsd/pages/pnl'));
const Home = lazy(() => import('./fsd/pages/home'));
const Landing = lazy(() => import('./fsd/pages/landing'));
const PrivacyPage = lazy(() => import('./fsd/pages/privacy/ui/PrivacyPage'));
const TermsPage = lazy(() => import('./fsd/pages/terms/ui/TermsPage'));
const PricePage = lazy(() => import('@fsd/pages/price'));
const Logistics = lazy(() => import('./fsd/pages/logistics'));
const EditPage = lazy(() =>
  import('./fsd/pages/logistics/ui/InnerPages/EditPage/index.jsx')
);
const SettingsPage = lazy(() =>
  import('./fsd/pages/logistics/ui/InnerPages/SettingsPage/index.jsx')
);
const NotFound = lazy(() => import('./fsd/pages/not-found'));
const Sails = lazy(() => import('./fsd/pages/sails/ui/index.jsx'));
const ServerError = lazy(() => import('./fsd/pages/server-error'));
const EmployeeCard = lazy(() => import('./fsd/pages/fot/ui/EmployeeCard.jsx'));
const Staff = lazy(() => import('./fsd/pages/fot'));
const Counterparty = lazy(() => import('./fsd/pages/counterparties'));
const Warehouse = lazy(() => import('./fsd/pages/warehouses'));
const PlanFact = lazy(() => import('./fsd/pages/plan-fact'));
const AcceptanceItem = lazy(() => import('./fsd/pages/acceptance-item'));
const AcceptanceList = lazy(() => import('./fsd/pages/acceptances'));
const OrderItem = lazy(() => import('./fsd/pages/order-item'));
const OrderList = lazy(() => import('./fsd/pages/orders'));
const ShipmentItem = lazy(() => import('./fsd/pages/shipment-item'));
const ShipmentList = lazy(() => import('./fsd/pages/shipments'));
const CashboxItem = lazy(() => import('./fsd/pages/cashbox-item'));
const CostPrice = lazy(() => import('./fsd/pages/cost-price'));
const TariffsAndPayments = lazy(() =>
  import('./fsd/pages/tariffs-and-payments')
);
const ClosingPeriods = lazy(() =>
  import('./fsd/pages/account/ui/Lc/ClosingPeriods/index.jsx')
);
const NotAllowed = lazy(() => import('./fsd/pages/not-allowed'));
const YandexCallback = lazy(() =>
  import('./fsd/pages/yandex-callback/ui/YandexCallback')
);
const SberOAuthConfirmPage = lazy(
  () => import('@fsd/pages/sber-oauth-confirm/')
);
const RNPPage = lazy(() => import('./fsd/pages/RNPPage'));

const LoggedInRoute = observer(() => {
  useLoadClosureMonths();
  useRealtimeNotifications();
  useGlobalAlertsNotifications();
  useProtectRoute();

  return (
    <>
      <TopNotification />
      <Outlet />
    </>
  );
});

const LoggedOutRoute = () => {
  const token = localStorage.getItem('token');

  return token ? <Navigate to={routes.pnl.path} /> : <Outlet />;
};

const fullContainerPages = [
  routes.balance.path,
  routes.pnl.path,
  routes.dds.path,
  routes.banks.path,
  routes.rules.path,
  routes.cashbox.path,
  routes.loans.path,
  routes.cashboxItem.path,
  routes.projects.path,
  routes.logistics.path,
  routes.costPrice.path,
  routes.projectItem.path,
  routes.home.path,
  routes.fot.path,
  routes.employeeCard.path,
];

function App() {
  const location = useLocation();
  const [fullContainer, setContainer] = React.useState(false);

  React.useEffect(() => {
    if (
      fullContainerPages.includes(location.pathname) ||
      location.pathname.includes('/analytics/projects/item/')
    ) {
      setContainer(true);
    } else {
      setContainer(false);
    }
  }, [location]);
  useEffect(() => {
    localStorage.removeItem('clicked');
  }, []);

  const isLandingLayout = [
    routes.home.path,
    routes.terms.path,
    routes.policy.path,
    routes.price.path,
  ].includes(location.pathname);

  return (
    <>
      {![
        routes.home.path,
        routes.policy.path,
        routes.terms.path,
        routes.price.path,
        routes.signup.path,
        routes.login.path,
        routes.yandexCallback.path,
        routes.passwordReset.path,
        routes.chooseCabinet.path,
      ].includes(location.pathname) && <Header full={fullContainer} />}

      <ErrorBoundary
        key={location.pathname}
        fallbackRender={({ resetErrorBoundary }) => (
          <Suspense fallback={<EmptyData desc={<Loader />} />}>
            <ServerError resetError={resetErrorBoundary} />
          </Suspense>
        )}
        onError={(error, info) => {
          console.error('ErrorBoundary', 'error', error, 'info', info);
        }}
      >
        <div
          className={cx(mainStyle['container-xxl'], mainStyle['page-content'])}
          style={{
            maxWidth: '100vw',
            padding: isLandingLayout ? '0' : undefined,
            paddingBottom: isLandingLayout ? '0' : '15px',
            height: isLandingLayout ? undefined : 'calc(100% - 55px)',
          }}
        >
          <Suspense fallback={<EmptyData desc={<Loader />} />}>
            <Routes>
              <Route path={routes.terms.path} element={<TermsPage />} />
              <Route path={routes.policy.path} element={<PrivacyPage />} />
              <Route path={routes.price.path} element={<PricePage />} />
              <Route path={routes.home.path} element={<LoggedOutRoute />}>
                <Route index element={<Landing />} />
                <Route
                  path={routes.passwordReset.path}
                  element={<PasswordReset />}
                />
                <Route path={routes.signup.path} element={<Signup />} />
                <Route path={routes.login.path} element={<Login />} />

                <Route
                  path={routes.yandexCallback.path}
                  element={<YandexCallback />}
                />
              </Route>

              <Route path={routes.home.path} element={<LoggedInRoute />}>
                <Route index element={<Home />} />
                <Route path={routes.rnp.path} element={<RNPPage />} />
                <Route path={routes.returns.path} element={<Returns />} />
                <Route
                  path={routes.chooseCabinet.path}
                  element={<ChooseCabinet />}
                />
                <Route path={routes.costPrice.path} element={<CostPrice />} />
                <Route
                  path={routes.pnl.path}
                  element={<FinancingReport setFull={setContainer} />}
                />
                <Route
                  path={routes.balance.path}
                  element={<Balance setFull={setContainer} />}
                />
                <Route path={routes.banks.path} element={<Banks />} />
                <Route path={routes.cashbox.path} element={<Cashboxes />} />
                <Route
                  path={routes.cashboxItem.path}
                  element={<CashboxItem />}
                />
                <Route path={routes.rules.path} element={<Rules />} />
                <Route path={routes.loans.path} element={<Loans />} />
                <Route path={routes.planfact.path} element={<PlanFact />} />

                <Route path={routes.orders.path} element={<OrderList />} />
                <Route path={routes.orderItem.path} element={<OrderItem />} />
                <Route
                  path={routes.acceptance.path}
                  element={<AcceptanceList />}
                />
                <Route
                  path={routes.acceptanceItem.path}
                  element={<AcceptanceItem />}
                />
                <Route path={routes.shipment.path} element={<ShipmentList />} />
                <Route
                  path={routes.shipmentItem.path}
                  element={<ShipmentItem />}
                />
                <Route path={routes.movement.path} element={<MovingList />} />
                <Route
                  path={routes.movementItem.path}
                  element={<MovingItem />}
                />
                <Route path={routes.remains.path} element={<Remains />} />

                <Route path={routes.logistics.path} element={<Logistics />} />
                <Route
                  path={routes.logisticsSettings.path}
                  element={<SettingsPage />}
                />
                <Route
                  path={routes.logisticsEdit.path}
                  element={<EditPage />}
                />
                <Route
                  path={routes.answeringMachine.path}
                  element={<Answering />}
                />
                <Route
                  path={routes.answeringMachineEdit.path}
                  element={<AnsweringEdit />}
                />

                <Route path="/settings" element={<SettingsLayout />}>
                  <Route path={routes.account.path} element={<Account />} />

                  <Route path={routes.organisations.path} element={<Api />} />

                  <Route
                    path={routes.sberOAuthConfirm.path}
                    element={<SberOAuthConfirmPage />}
                  />

                  <Route
                    path={routes.articleManagement.path}
                    element={<Articles />}
                  />

                  <Route
                    path={routes.closingPeriods.path}
                    element={<ClosingPeriods />}
                  />

                  <Route path={routes.staffs.path} element={<AccountStaff />} />

                  <Route
                    path={routes.counterparties.path}
                    element={<Counterparty/>}
                  />

                  <Route path={routes.warehouses.path} element={<Warehouse />} />

                  <Route path={routes.notices.path} element={<Notices />} />
                </Route>

                <Route path={routes.dds.path} element={<DDS />} />
                <Route path={routes.projects.path} element={<Projects />} />
                <Route
                  path={routes.projectItem.path}
                  element={<ProjectItem />}
                />
                <Route path={routes.fot.path} element={<Staff />} />
                <Route
                  path={routes.employeeCard.path}
                  element={<EmployeeCard />}
                />
                <Route
                  path={routes.advertisement.path}
                  element={<Advertisement />}
                />
                <Route path={routes.economy.path} element={<Economy />} />
                <Route path={routes.sails.path} element={<Sails />} />
                <Route path={routes.faq.path} element={<Faq />} />
                <Route path={routes.faqArticle.path} element={<Article />} />
                <Route
                  path={routes.faqCategories.path}
                  element={<Categories />}
                />
                <Route
                  path={routes.tariffsAndPayments.path}
                  element={<TariffsAndPayments />}
                />
                <Route
                  path={routes.requestsForSupply.path}
                  element={<RequestsForSupply />}
                />
                <Route
                  path={routes.requestsForSupplyItem.path}
                  element={<RequestsForSupplyItem />}
                />
                <Route
                  path={routes.mutualSettlements.path}
                  element={<MutualSettlements />}
                />
                <Route
                  path={routes.serverError.path}
                  element={<ServerError />}
                />
                <Route path={routes.notAllowed.path} element={<NotAllowed />} />
                <Route path={routes.notFound.path} element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </div>
      </ErrorBoundary>
    </>
  );
}

export default App;
